import { Stack } from '@mui/material';
import { t } from 'i18next';
import { CallsTableController } from '../../../common/calls/controllers/CallsTableController';
import { Call } from '../../../common/calls/models/Call';
import { BackButton } from '../../../common/components/BackButton';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { SshHandshake } from '../../../common/handshakes/models/SshHandshake';
import { TlsHandshake } from '../../../common/handshakes/models/TlsHandshake';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { Instance } from '../../../common/instances/models/Instance';
import { PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone } from '../../common/models/Report';
import { OperationInstancesTable } from '../components/OperationInstancesTable';
import { OperationKeysTable } from '../components/OperationKeysTable';
import { OperationSshHandshakesTable } from '../components/OperationSshHandshakesTable';
import { OperationSummary } from '../components/OperationSummary';
import { OperationTlsHandshakesTable } from '../components/OperationTlsHandshakesTable';
import { OperationDetail } from '../models/OperationDetail';
import { OperationKey } from '../models/valueObjects/OperationKey';
import { DEFAULT_OPERATION_CALLS_PAGE_SIZE, OperationRepository } from '../services/OperationRepository';

type State = BaseState & {
  loading: boolean;
};

type Props = WithRouterProps<ReportOutletContextType> & {};

class Controller extends BaseController<Props, State> {
  private readonly operationRepository = new OperationRepository();

  private readonly reportId: number;
  private readonly operationId: number;

  private operation: OperationDetail | null = null;
  private initialCalls: PaginationResult<Call> | null = null;
  private initialTlsHandshakes: PaginationResult<TlsHandshake> | null = null;
  private initialSshHandshakes: PaginationResult<SshHandshake> | null = null;
  private initialInstances: PaginationResult<Instance> | null = null;
  private initialKeys: PaginationResult<OperationKey> | null = null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ reportId: this.reportId, operationId: this.operationId } = this.parseNumericIds(['reportId', 'operationId']));

    this.state = { ...super.getBaseState(), loading: true };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.operationRepository.cancelAllRequests();
  }

  render() {
    // Each sub-controller is displayed during the initial load (in skeleton mode),
    // then only if there is any content available
    const showKeysTable = !this.initialKeys || this.initialKeys.data.length > 0;
    const showTlsHandshakesTable = !this.initialTlsHandshakes || this.initialTlsHandshakes.data.length > 0;
    const showSshHandshakesTable = !this.initialSshHandshakes || this.initialSshHandshakes.data.length > 0;
    const showInstancesTable = !this.initialInstances || this.initialInstances.data.length > 0;
    const showCallsTable = !this.initialCalls || this.initialCalls.data.length > 0;

    return this.renderBase(
      <Stack gap={2}>
        <BackButton />
        <OperationSummary operationId={this.operationId} operation={this.operation} />
        {showTlsHandshakesTable && (
          <OperationTlsHandshakesTable initialData={this.initialTlsHandshakes} operationId={this.operationId} parentLoading={this.state.loading} />
        )}
        {showSshHandshakesTable && (
          <OperationSshHandshakesTable initialData={this.initialSshHandshakes} operationId={this.operationId} parentLoading={this.state.loading} />
        )}
        {showKeysTable && (
          <OperationKeysTable initialData={this.initialKeys} reportId={this.reportId} operationId={this.operationId} parentLoading={this.state.loading} />
        )}
        {showInstancesTable && (
          <OperationInstancesTable
            reportId={this.reportId}
            operationId={this.operationId}
            parentLoading={this.state.loading}
            initialInstances={this.initialInstances}
          />
        )}
        {showCallsTable && (
          <CallsTableController
            variant={ModelType.Operation}
            variantId={this.operationId}
            defaultPageSize={DEFAULT_OPERATION_CALLS_PAGE_SIZE}
            traceType={this.operation?.getTraceType() ?? undefined}
            projectId={this.operation?.getProjectId() ?? undefined}
            initialCalls={this.initialCalls}
            parentLoading={this.state.loading}
          />
        )}
      </Stack>
    );
  }

  private async fetchData() {
    try {
      this.setState({ loading: true });
      const { operation, initialCalls, initialTlsHandshakes, initialSshHandshakes, initialInstances, initialKeys } =
        await this.operationRepository.getOperation(this.reportId, this.operationId);
      this.operation = operation;
      this.initialCalls = initialCalls;
      this.initialTlsHandshakes = initialTlsHandshakes;
      this.initialSshHandshakes = initialSshHandshakes;
      this.initialInstances = initialInstances;
      this.initialKeys = initialKeys;
      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingOperation') });
    }
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed, ReportController will redirect to Operations
      // It would not make sense to try and load the same `operationId` on a different Report
      return;
    }

    if (currReport instanceof ReportDone) {
      // Do nothing unless the new Report is a ReportDone
      this.fetchData();
    }
  }
}

export const OperationController = withRouter<ReportOutletContextType>(Controller);
